// src/components/PushNotificationToggle.js
// 帳號選單裡的「開啟/關閉新訂位通知」開關 + 「傳送測試通知」。
// 自帶狀態管理；結果透過 onNotify(message, severity) 往外丟（讓 Snackbar 在選單關掉後仍看得到）。
// 開了之後，有新訂位（客人網頁 / AI 電話 / 後台手動）就會推播到此裝置的鎖屏/桌面。
import React, { useState, useEffect } from 'react';
import { MenuItem, ListItemIcon, CircularProgress } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import SendIcon from '@mui/icons-material/Send';
import {
  pushSupported,
  isPushEnabled,
  isStandalonePWA,
  enablePush,
  disablePush,
  sendTestPush
} from '../services/pushNotificationService';

const isIOS = () =>
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const PushNotificationToggle = ({ onNotify = () => {}, onAfterAction = () => {} }) => {
  const [supported] = useState(pushSupported());
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    isPushEnabled().then(setEnabled).catch(() => {});
  }, [supported]);

  // iOS 只有「加到主畫面」的 PWA 才支援
  if (!supported) {
    if (isIOS() && !isStandalonePWA()) {
      return (
        <MenuItem
          onClick={() => {
            onNotify('iPhone 請先用 Safari「加入主畫面」，再從主畫面開啟才能開通知', 'info');
            onAfterAction();
          }}
        >
          <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
          新訂位通知（iPhone 需先加到主畫面）
        </MenuItem>
      );
    }
    return (
      <MenuItem disabled>
        <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
        此裝置不支援推播通知
      </MenuItem>
    );
  }

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        onNotify('已關閉新訂位通知', 'success');
      } else {
        await enablePush();
        setEnabled(true);
        onNotify('已開啟新訂位通知，有新訂位會即時通知這台裝置', 'success');
      }
    } catch (e) {
      onNotify(e?.message || '操作失敗', 'error');
    } finally {
      setBusy(false);
      onAfterAction();
    }
  };

  const handleTest = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await sendTestPush();
      const sent = res?.sent ?? 0;
      onNotify(sent > 0 ? `已送出測試通知（${sent} 台裝置）` : '已呼叫測試，但目前沒有已訂閱的裝置', sent > 0 ? 'success' : 'info');
    } catch (e) {
      onNotify(e?.message || '測試推播失敗', 'error');
    } finally {
      setBusy(false);
      onAfterAction();
    }
  };

  return (
    <>
      <MenuItem onClick={handleToggle} disabled={busy}>
        <ListItemIcon>
          {busy ? <CircularProgress size={18} />
            : enabled ? <NotificationsOffIcon fontSize="small" />
            : <NotificationsActiveIcon fontSize="small" />}
        </ListItemIcon>
        {enabled ? '關閉新訂位通知' : '開啟新訂位通知'}
      </MenuItem>
      {enabled && (
        <MenuItem onClick={handleTest} disabled={busy}>
          <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
          傳送測試通知
        </MenuItem>
      )}
    </>
  );
};

export default PushNotificationToggle;
